import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Header } from '../../components/Layout';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { formatDate, formatDateTime } from '../../lib/utils';
import { toast } from 'sonner';
import {
  Settings,
  User,
  Building2,
  CreditCard,
  Moon,
  Sun,
  Monitor,
  History,
  Loader2,
  CheckCircle,
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function SettingsPage() {
  const { user } = useAuth();
  const { theme, setTheme } = useTheme();
  const [auditLogs, setAuditLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(true);

  useEffect(() => {
    fetchAuditLogs();
  }, []);

  const fetchAuditLogs = async () => {
    try {
      const response = await axios.get(`${API_URL}/audit-logs?limit=20`);
      setAuditLogs(response.data);
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
    } finally {
      setLoadingLogs(false);
    }
  };

  const themeOptions = [
    { value: 'light', label: 'Light', icon: Sun },
    { value: 'dark', label: 'Dark', icon: Moon },
    { value: 'system', label: 'System', icon: Monitor },
  ];

  const getActionLabel = (action) => {
    const labels = {
      create: 'Created',
      update: 'Updated',
      delete: 'Deleted',
      status_change: 'Status Changed',
    };
    return labels[action] || action;
  };

  const getEntityLabel = (entityType) => {
    const labels = {
      worker: 'Worker',
      trucker: 'Trucker',
      rate: 'Rate',
      payroll_period: 'Payroll Period',
      payroll_entry: 'Payroll Entry',
      trucker_payroll_entry: 'Trucker Entry',
      ssb_rule: 'SSB Rule',
    };
    return labels[entityType] || entityType;
  };

  return (
    <div className="flex flex-col min-h-screen">
      <Header
        title="Settings"
        subtitle="Manage your account and preferences"
      />

      <div className="flex-1 p-4 lg:p-8 space-y-6">
        {/* Profile Section */}
        <Card className="card-base animate-slide-up" style={{ animationDelay: '0ms' }}>
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <User className="w-5 h-5 text-primary" />
              Profile Information
            </CardTitle>
            <CardDescription>Your account details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center">
                <span className="text-2xl font-bold text-primary-foreground">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <h3 className="text-xl font-semibold">{user?.name}</h3>
                <p className="text-muted-foreground">{user?.email}</p>
                <Badge variant="outline" className="mt-2 capitalize">
                  {user?.role}
                </Badge>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 pt-4 border-t border-border">
              {user?.company_name && (
                <div className="flex items-center gap-3">
                  <Building2 className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Farm Group</p>
                    <p className="font-medium">{user.company_name}</p>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-3">
                <CreditCard className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Subscription</p>
                  <p className="font-medium capitalize">{user?.subscription_tier || 'Free'} Tier</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Theme Section */}
        <Card className="card-base animate-slide-up" style={{ animationDelay: '50ms' }}>
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Settings className="w-5 h-5 text-secondary" />
              Appearance
            </CardTitle>
            <CardDescription>Customize how Cane Pay looks</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {themeOptions.map((option) => {
                const Icon = option.icon;
                const isActive = theme === option.value;
                return (
                  <Button
                    key={option.value}
                    variant={isActive ? 'default' : 'outline'}
                    className="gap-2"
                    onClick={() => setTheme(option.value)}
                    data-testid={`theme-btn-${option.value}`}
                  >
                    <Icon className="w-4 h-4" />
                    {option.label}
                    {isActive && <CheckCircle className="w-4 h-4" />}
                  </Button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Audit Logs Section */}
        <Card className="card-base animate-slide-up" style={{ animationDelay: '100ms' }}>
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <History className="w-5 h-5 text-accent" />
              Recent Activity
            </CardTitle>
            <CardDescription>Your recent actions in the system</CardDescription>
          </CardHeader>
          <CardContent>
            {loadingLogs ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : auditLogs.length > 0 ? (
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {auditLogs.map((log) => (
                  <div
                    key={log.id}
                    className="flex items-center justify-between p-3 rounded-lg bg-muted/30 border border-border/50"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${
                        log.action === 'create' ? 'bg-green-500' :
                        log.action === 'update' ? 'bg-blue-500' :
                        log.action === 'delete' ? 'bg-red-500' :
                        'bg-yellow-500'
                      }`} />
                      <div>
                        <p className="text-sm">
                          <span className="font-medium">{getActionLabel(log.action)}</span>
                          {' '}
                          <span className="text-muted-foreground">{getEntityLabel(log.entity_type)}</span>
                        </p>
                        {log.details && (
                          <p className="text-xs text-muted-foreground">
                            {log.details.name && `Name: ${log.details.name}`}
                            {log.details.from && log.details.to && ` (${log.details.from} → ${log.details.to})`}
                          </p>
                        )}
                      </div>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {formatDateTime(log.timestamp)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <History className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No activity recorded yet.</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
